export default {
  name: 'yotpoReviews',
  component(config) {
    return {
      // Configuration
      appKey: config.appKey,
      productId: config.productId,
      productTitle: config.productTitle,
      perPage: config.perPage || 10,

      // State
      reviewData: null,
      currentReviews: [],
      currentPage: 1,
      loading: false,
      hasMoreReviews: false,
      retainedCustomerVotes: sessionStorage.getItem('retainedCustomerVotes')
        ? JSON.parse(sessionStorage.getItem('retainedCustomerVotes'))
        : {},
      currentVotes: {},
      voteError: false,
      showForm: false,
      recommendationPercentage: 0,

      // Filters and sorting - consolidated into single sortOption
      sortOption: 'date-desc',
      starFilter: '',

      // Initialization
      init() {
        this.loadReviews(1);
        this.calculateRecommendationPercentage();
      },

      // Computed properties for backward compatibility
      get sortBy() {
        return this.sortOption.split('-')[0];
      },

      get sortDirection() {
        return this.sortOption.split('-')[1];
      },

      // API Methods
      async loadReviews(page = 1) {
        this.loading = true;
        this.currentPage = page;

        try {
          const params = new URLSearchParams({
            per_page: this.perPage,
            page: page,
            sort: this.sortBy,
            direction: this.sortDirection
          });

          if (this.starFilter) {
            params.append('star', this.starFilter);
          }

          const response = await fetch(
            `https://api-cdn.yotpo.com/v1/widget/${this.appKey}/products/${this.productId}/reviews.json?${params}`
          );

          if (!response.ok) {
            throw new Error('Failed to fetch reviews');
          }

          const data = await response.json();

          if (page === 1) {
            this.reviewData = data.response;
            this.currentReviews = data.response.reviews || [];
            this.currentVotes = data.response.reviews.reduce((prev, curr) => {
              prev[curr.id] = {
                up: (curr.votes_up || 0) + (this.retainedCustomerVotes[curr.id]?.up || 0),
                down: (curr.votes_down || 0) + (this.retainedCustomerVotes[curr.id]?.down || 0)
              };

              return prev;
            }, {});
          } else {
            this.currentReviews.push(...(data.response.reviews || []));

            this.currentVotes = data.response.reviews.reduce(
              (prev, curr) => {
                prev[curr.id] = {
                  up: (curr.votes_up || 0) + (this.retainedCustomerVotes[curr.id]?.up || 0),
                  down: (curr.votes_down || 0) + (this.retainedCustomerVotes[curr.id]?.down || 0)
                };

                return prev;
              },
              { ...this.currentVotes }
            );
          }

          let totalReviews = this.reviewData.bottomline.total_review;
          if (this.starFilter) {
            totalReviews = this.reviewData.bottomline.star_distribution[this.starFilter] || 0;
          }

          this.hasMoreReviews = this.currentReviews.length < totalReviews;
        } catch (error) {
          console.error('Error loading reviews:', error);
        } finally {
          this.loading = false;
        }
      },

      async loadMoreReviews() {
        if (this.hasMoreReviews && !this.loading) {
          await this.loadReviews(this.currentPage + 1);
        }
      },

      async voteReview(reviewId, voteType) {
        if (!this.retainedCustomerVotes[reviewId]) {
          this.retainedCustomerVotes[reviewId] = { up: 0, down: 0 };
        }

        let voteUp = false;
        let voteDown = false;
        let undoUp = false;
        let undoDown = false;

        if (voteType === 'up') {
          if (this.retainedCustomerVotes[reviewId].up > 0) {
            undoUp = true;
          } else {
            voteUp = true;

            if (this.retainedCustomerVotes[reviewId].down > 0) {
              undoDown = true;
            }
          }
        } else {
          if (this.retainedCustomerVotes[reviewId].down > 0) {
            undoDown = true;
          } else {
            voteDown = true;

            if (this.retainedCustomerVotes[reviewId].up > 0) {
              undoUp = true;
            }
          }
        }

        const requestConfig = {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          }
        };

        const voteUpPromise = voteUp ? fetch(this.getVoteUrl(reviewId, 'up', false), requestConfig) : null;
        const voteDownPromise = voteDown ? fetch(this.getVoteUrl(reviewId, 'down', false), requestConfig) : null;
        const undoVoteUpPromise = undoUp ? fetch(this.getVoteUrl(reviewId, 'up', true), requestConfig) : null;
        const undoVoteDownPromise = undoDown ? fetch(this.getVoteUrl(reviewId, 'down', true), requestConfig) : null;

        const [voteUpRes, voteDownRes, undoVoteUpRes, undoVoteDownRes] = await Promise.all([
          voteUpPromise,
          voteDownPromise,
          undoVoteUpPromise,
          undoVoteDownPromise
        ]);

        if (voteUpRes) {
          if (voteUpRes.ok) {
            if (voteUp) {
              this.retainedCustomerVotes[reviewId].up += 1;
              this.currentVotes[reviewId].up += 1;
            }
          } else {
            console.error('Failed to vote up');
            this.voteError = true;
          }
        }

        if (voteDownRes) {
          if (voteDownRes.ok) {
            if (voteDown) {
              this.retainedCustomerVotes[reviewId].down += 1;
              this.currentVotes[reviewId].down += 1;
            }
          } else {
            console.error('Failed to vote down');
            this.voteError = true;
          }
        }

        if (undoVoteUpRes) {
          if (undoVoteUpRes.ok) {
            if (undoUp) {
              this.retainedCustomerVotes[reviewId].up = 0;

              if (this.currentVotes[reviewId].up > 0) {
                this.currentVotes[reviewId].up -= 1;
              }
            }
          } else {
            console.error('Failed to undo vote up');
            this.voteError = true;
          }
        }

        if (undoVoteDownRes) {
          if (undoVoteDownRes.ok) {
            if (undoDown) {
              this.retainedCustomerVotes[reviewId].down = 0;

              if (this.currentVotes[reviewId].down > 0) {
                this.currentVotes[reviewId].down -= 1;
              }
            }
          } else {
            console.error('Failed to undo vote down');
            this.voteError = true;
          }
        }

        sessionStorage.setItem('retainedCustomerVotes', JSON.stringify(this.retainedCustomerVotes));

        if (this.voteError) {
          window.dispatchEvent(
            new CustomEvent('arc:action:showNotification', {
              detail: {
                message: 'Error occurred while voting. Please try again later.',
                type: 'error',
                duration: 3000
              }
            })
          );
        }

        setTimeout(() => {
          this.voteError = false;
        }, 3000);
      },

      openForm() {
        // this.showForm = true;
        // document.body.style.overflow = 'hidden';

        // Open OOTB Yotpo review form
        document.querySelector('#yotpo-main-widget-btn')?.click();
      },

      closeForm() {
        this.showForm = false;
        document.body.style.overflow = '';
      },

      // Utility Methods
      generateStars(rating, starSize = 16, starColor = '#000000') {
        const fullStars = Math.floor(rating);
        const hasHalfStar = rating % 1 !== 0;
        const emptyStars = 5 - fullStars - (hasHalfStar ? 1 : 0);

        let stars = '';

        // Full stars
        for (let i = 0; i < fullStars; i++) {
          stars += `
            <svg
              width="${starSize}px"
              height="${starSize}px"
              viewBox="0 -0.5 33 33"
              version="1.1"
              xmlns="http://www.w3.org/2000/svg"
              xmlns:xlink="http://www.w3.org/1999/xlink"
            >
              <g
                id="Vivid.JS"
                stroke="none"
                stroke-width="1"
                fill="none"
                fill-rule="evenodd"
              >
                <g
                  id="Vivid-Icons"
                  transform="translate(-903.000000, -411.000000)"
                  fill="${starColor}"
                  class="!fill-[var(--fill-color)]"
                >
                  <g
                    id="Icons"
                    transform="translate(37.000000, 169.000000)"
                  >
                    <g
                      id="star"
                      transform="translate(858.000000, 234.000000)"
                    >
                      <g
                        transform="translate(7.000000, 8.000000)"
                        id="Shape"
                      >
                        <polygon
                          points="27.865 31.83 17.615 26.209 7.462 32.009 9.553 20.362 0.99 12.335 12.532 10.758 17.394 0 22.436 10.672 34 12.047 25.574 20.22"
                        ></polygon>
                      </g>
                    </g>
                  </g>
                </g>
              </g>
            </svg>
          `;
        }

        // Half star
        if (hasHalfStar) {
          stars += `
            <svg
              width="${starSize}px"
              height="${starSize}px"
              viewBox="0 -0.5 33 33"
              version="1.1"
              xmlns="http://www.w3.org/2000/svg"
              xmlns:xlink="http://www.w3.org/1999/xlink"
            >
              <defs>
                <linearGradient id="halfFillGradient2" x1="0%" y1="0%" x2="100%" y2="0%">
                  <stop offset="0%" stop-color="var(--fill-color)" />
                  <stop offset="50%" stop-color="var(--fill-color)" />
                  <stop offset="50%" stop-color="transparent" />
                  <stop offset="100%" stop-color="transparent" />
                </linearGradient>
              </defs>
              <g
                id="Vivid.JS"
                stroke="none"
                stroke-width="1"
                fill="none"
                fill-rule="evenodd"
              >
                <g
                  id="Vivid-Icons"
                  transform="translate(-903.000000, -411.000000)"
                  stroke="${starColor}"
                  stroke-width="2"
                  fill="url(#halfFillGradient2)"
                  class="!fill-[url(#halfFillGradient2)] !stroke-[var(--fill-color)]"
                >
                  <g
                    id="Icons"
                    transform="translate(37.000000, 169.000000)"
                  >
                    <g
                      id="star"
                      transform="translate(858.000000, 234.000000)"
                    >
                      <g
                        transform="translate(7.000000, 8.000000)"
                        id="Shape"
                      >
                        <polygon
                          points="27.865 31.83 17.615 26.209 7.462 32.009 9.553 20.362 0.99 12.335 12.532 10.758 17.394 0 22.436 10.672 34 12.047 25.574 20.22"
                        ></polygon>
                      </g>
                    </g>
                  </g>
                </g>
              </g>
            </svg>
          `;
        }

        // Empty stars
        for (let i = 0; i < emptyStars; i++) {
          stars += `
            <svg
              width="${starSize}px"
              height="${starSize}px"
              viewBox="0 -0.5 33 33"
              version="1.1"
              xmlns="http://www.w3.org/2000/svg"
              xmlns:xlink="http://www.w3.org/1999/xlink"
            >
              <g
                id="Vivid.JS"
                stroke="none"
                stroke-width="1"
                fill="none"
                fill-rule="evenodd"
              >
                <g
                  id="Vivid-Icons"
                  transform="translate(-903.000000, -411.000000)"
                  stroke="${starColor}"
                  stroke-width="2"
                  fill="none"
                  class="!stroke-[var(--fill-color)]"
                >
                  <g
                    id="Icons"
                    transform="translate(37.000000, 169.000000)"
                  >
                    <g
                      id="star"
                      transform="translate(858.000000, 234.000000)"
                    >
                      <g
                        transform="translate(7.000000, 8.000000)"
                        id="Shape"
                      >
                        <polygon
                          points="27.865 31.83 17.615 26.209 7.462 32.009 9.553 20.362 0.99 12.335 12.532 10.758 17.394 0 22.436 10.672 34 12.047 25.574 20.22"
                        ></polygon>
                      </g>
                    </g>
                  </g>
                </g>
              </g>
            </svg>
          `;
        }

        return stars;
      },

      formatDate(dateString) {
        const date = new Date(dateString);
        const now = new Date();
        const diffInDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));

        if (diffInDays === 0) {
          return 'Today';
        } else if (diffInDays === 1) {
          return 'Yesterday';
        } else if (diffInDays < 30) {
          return `${diffInDays} days ago`;
        } else if (diffInDays < 365) {
          const months = Math.floor(diffInDays / 30);
          return `${months} month${months > 1 ? 's' : ''} ago`;
        } else {
          const years = Math.floor(diffInDays / 365);
          return `${years} year${years > 1 ? 's' : ''} ago`;
        }
      },

      insertDynamicText(text, type, data) {
        const normalizedText = text.replace(/\{\s*([^}]+?)\s*\}/g, (match, p1) => `{${p1.trim()}}`);
        return normalizedText.replace(type, data);
      },

      getVoteUrl(reviewId, voteType, undo) {
        return `http://api.yotpo.com/reviews/${reviewId}/vote/${voteType}${undo ? '/true' : ''}`;
      },

      isRecommended(review) {
        const recommendationField = this.findRecommendationField(review.custom_fields || {});
        return recommendationField ? recommendationField[1].value === 'Yes' : false;
      },

      findRecommendationField(customFields) {
        return Object.entries(customFields).find(([fieldId, fieldData]) => {
          return fieldId === '--83499' || (fieldData.title && fieldData.title.toLowerCase().includes('recommendation'));
        });
      },

      async calculateRecommendationPercentage() {
        try {
          const response = await fetch(
            `https://api-cdn.yotpo.com/v1/widget/${this.appKey}/products/${this.productId}/reviews.json?per_page=150`
          );

          if (!response.ok) {
            throw new Error('Failed to fetch reviews');
          }

          const data = await response.json();

          const recommendationResponses = data.response.reviews
            .map((review) => {
              const recommendationField = this.findRecommendationField(review.custom_fields || {});
              return recommendationField ? recommendationField[1].value : null;
            })
            .filter((value) => value === 'Yes' || value === 'No');

          if (recommendationResponses.length === 0) return 0;

          const yesCount = recommendationResponses.filter((value) => value === 'Yes').length;
          this.recommendationPercentage = Math.round((yesCount / recommendationResponses.length) * 100);
        } catch (error) {
          console.error('Error calculating recommendation percentage:', error);
          this.recommendationPercentage = 0;
        }
      }
    };
  }
};
